import React, { useMemo, useState, useEffect } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import startCase from 'lodash/startCase'

import { HydraTable, Heading, TimeAgo } from '@peracto/peracto-ui'

import { useConfig } from '@peracto/peracto-config'
import { GET_LIST, useClient } from '@peracto/client'
import { useParams } from 'react-router-dom'

const ProductImage = ({ resources, name }) => {
    let img = null

    resources?.forEach(resource => {
        if (
            !img ||
            (resource.productResource && resource.productResource?.displayOrder < img.displayOrder)
        ) {
            img = resource.productResource
        }
    })

    return !!img?.location ? (
        <img
            src={img?.location}
            title={name}
            alt={name}
            style={{ height: '40px', width: '40px' }}
        />
    ) : (
        <FontAwesomeIcon
            icon={faImage}
            style={{ width: '40px', height: '40px', color: '#606060' }}
        />
    )
}
const ProductList = () => {
    const [redirect, setRedirect] = useState()
    const { id } = useParams()
    const { client } = useClient()

    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const { products } = config.get('features', {})

    useEffect(() => {
        if (!products?.productVariants) {
            setRedirect(`/products/${id}`)
        }
    }, [products])

    // The 'selectable' property is used to define if the
    // column is available in the 'manage columns' dropdown
    const columns = useMemo(() => [
        {
            accessor: 'resources',
            Cell: row => {
                const splitProductIRI = row.row.original.product['@id'].split('/')
                const productId = splitProductIRI[splitProductIRI.length - 1]

                return (
                    <Link
                        to={`/products/${productId}/variants/${row.row.original.originId}`}
                        data-testid={`row-${row.row.index}-image`}
                    >
                        <ProductImage resources={row.cell.value} name={row.row.values.name} />
                    </Link>
                )
            },
            selectable: false,
        },
        {
            Header: 'SKU',
            accessor: 'sku',
            Cell: row => {
                const splitProductIRI = row.row.original.product['@id'].split('/')
                const productId = splitProductIRI[splitProductIRI.length - 1]

                return (
                    <Link
                        to={`/products/${productId}/variants/${row.row.original.originId}`}
                        data-testid={`row-${row.row.index}-sku`}
                    >
                        {row.cell.value}
                    </Link>
                )
            },
        },
        {
            Header: 'Availability',
            accessor: 'availability',
            Cell: ({ cell: { value } }) => startCase(value),
            selectable: false,
        },
        {
            Header: 'Status',
            accessor: 'status',
            sortType: 'basic',
            Cell: ({ cell: { value } }) => (
                <span
                    className={classNames('badge', 'badge-pill', {
                        'badge-info': value === 'draft',
                        'badge-success': value === 'active',
                        'badge-danger': value === 'deleted',
                    })}
                >
                    {startCase(value)}
                </span>
            ),
            selectable: false,
        },
        {
            Header: 'Last Modified',
            accessor: 'modifiedAt',
            sortType: 'basic',
            Cell: ({ cell: { value } }) =>
                value ? <TimeAgo title={formatDate(new Date(value))} date={value} /> : '-',
            selectable: false,
        },
        {
            Header: 'Default Variant',
            accessor: 'default',
            sortType: 'basic',
            Cell: ({ cell: { value } }) => (
                <>
                    {!!value ? <span className={'badge badge-pill badge-info'}>Default</span> : '-'}
                </>
            ),
            selectable: false,
        },
    ])

    const onFetchData = options => client(GET_LIST, 'product-variants', options)

    return (
        <>
            {redirect && <Redirect to={redirect} />}
            <Helmet>
                <title>Product Variants | Peracto</title>
            </Helmet>
            <Heading name="Product Variants">
                <div className="!flex !items-center !ml-2">
                    <Link
                        className="btn btn-outline-primary !mr-2"
                        to={`/products/${id}`}
                        data-testid="back-to-product"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Product
                    </Link>

                    <Link
                        className="btn btn-primary"
                        to={`/products/${id}/variants/add`}
                        data-testid="add-product-variant"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                        Add Product Variant
                    </Link>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                onFetchData={onFetchData}
                                alwaysFilter={{
                                    product: id,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProductList
